// src/services/companySettings.service.ts
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  CompanySettings,
  ExpenseCategory,
  DEFAULT_EXPENSE_CATEGORIES
} from '../types/finance.types';

const SETTINGS_DOC_ID = 'company_settings';

export async function getCompanySettings(): Promise<CompanySettings | null> {
  try {
    const docRef = doc(db, 'settings', SETTINGS_DOC_ID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as CompanySettings;
    }
    return null;
  } catch (error) {
    console.error('Error getting company settings:', error);
    throw error;
  }
}

export async function saveCompanySettings(
  settings: Partial<CompanySettings>,
  userId: string
): Promise<CompanySettings> {
  try {
    const docRef = doc(db, 'settings', SETTINGS_DOC_ID);
    const existingDoc = await getDoc(docRef);

    let updatedSettings: CompanySettings;

    if (existingDoc.exists()) {
      // Update existing settings
      const existing = existingDoc.data() as CompanySettings;
      updatedSettings = {
        ...existing,
        ...settings,
        updatedAt: Timestamp.now(),
        updatedBy: userId,
      };
    } else {
      // Create new settings with defaults
      updatedSettings = {
        id: SETTINGS_DOC_ID,
        companyName: settings.companyName || '',
        companyAddress: settings.companyAddress || '',
        companyCity: settings.companyCity || '',
        companyState: settings.companyState || '',
        companyZip: settings.companyZip || '',
        companyPhone: settings.companyPhone || '',
        companyEmail: settings.companyEmail || '',
        companyWebsite: settings.companyWebsite || '',
        taxId: settings.taxId || '',
        bankName: settings.bankName || '',
        bankAccountName: settings.bankAccountName || '',
        bankAccountNumber: settings.bankAccountNumber || '',
        bankRoutingNumber: settings.bankRoutingNumber || '',
        bankSwiftCode: settings.bankSwiftCode || '',
        bankAddress: settings.bankAddress || '',
        invoicePrefix: settings.invoicePrefix || 'INV-',
        nextInvoiceNumber: settings.nextInvoiceNumber || 1,
        defaultPaymentTerms: settings.defaultPaymentTerms || 30,
        invoiceNotes: settings.invoiceNotes || '',
        invoiceFooter: settings.invoiceFooter || '',
        expenseCategories: settings.expenseCategories || DEFAULT_EXPENSE_CATEGORIES,
        updatedAt: Timestamp.now(),
        updatedBy: userId,
        ...settings,
      };
    }

    await setDoc(docRef, updatedSettings);
    return updatedSettings;
  } catch (error) {
    console.error('Error saving company settings:', error);
    throw error;
  }
}

export async function initializeCompanySettings(userId: string): Promise<CompanySettings> {
  const existing = await getCompanySettings();
  if (existing) {
    return existing;
  }

  const defaultSettings: CompanySettings = {
    id: SETTINGS_DOC_ID,
    companyName: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyZip: '',
    companyPhone: '',
    companyEmail: '',
    companyWebsite: '',
    taxId: '',
    bankName: '',
    bankAccountName: '',
    bankAccountNumber: '',
    bankRoutingNumber: '',
    bankSwiftCode: '',
    bankAddress: '',
    invoicePrefix: 'INV-',
    nextInvoiceNumber: 1,
    defaultPaymentTerms: 30,
    invoiceNotes: '',
    invoiceFooter: '',
    expenseCategories: DEFAULT_EXPENSE_CATEGORIES,
    updatedAt: Timestamp.now(),
    updatedBy: userId,
  };

  const docRef = doc(db, 'settings', SETTINGS_DOC_ID);
  await setDoc(docRef, defaultSettings);
  return defaultSettings;
}

export async function addExpenseCategory(
  category: Omit<ExpenseCategory, 'id'>,
  userId: string
): Promise<void> {
  const settings = await getCompanySettings();
  if (!settings) {
    throw new Error('Company settings not found');
  }

  const newCategory: ExpenseCategory = {
    ...category,
    id: `custom_${Date.now()}`,
  };

  await saveCompanySettings(
    {
      expenseCategories: [...settings.expenseCategories, newCategory],
    },
    userId
  );
}

export async function updateExpenseCategory(
  categoryId: string,
  updates: Partial<ExpenseCategory>,
  userId: string
): Promise<void> {
  const settings = await getCompanySettings();
  if (!settings) {
    throw new Error('Company settings not found');
  }

  const updatedCategories = settings.expenseCategories.map((cat) =>
    cat.id === categoryId ? { ...cat, ...updates } : cat
  );

  await saveCompanySettings(
    {
      expenseCategories: updatedCategories,
    },
    userId
  );
}

export async function toggleExpenseCategoryStatus(
  categoryId: string,
  userId: string
): Promise<void> {
  const settings = await getCompanySettings();
  if (!settings) {
    throw new Error('Company settings not found');
  }

  const updatedCategories = settings.expenseCategories.map((cat) =>
    cat.id === categoryId ? { ...cat, isActive: !cat.isActive } : cat
  );

  await saveCompanySettings(
    {
      expenseCategories: updatedCategories,
    },
    userId
  );
}

export async function getNextInvoiceNumber(userId: string): Promise<string> {
  const settings = await getCompanySettings();
  if (!settings) {
    throw new Error('Company settings not found. Please configure settings first.');
  }

  const currentNumber = settings.nextInvoiceNumber;
  const year = new Date().getFullYear();
  const invoiceNumber = `${settings.invoicePrefix}${year}-${String(currentNumber).padStart(4, '0')}`;

  // Increment for next time
  await saveCompanySettings(
    {
      nextInvoiceNumber: currentNumber + 1,
    },
    userId
  );

  return invoiceNumber;
}

export async function getBankDetailsForInvoice(): Promise<{
  bankName: string;
  accountName: string;
  accountNumber: string;
  routingNumber: string;
  swiftCode?: string;
  bankAddress?: string;
} | null> {
  const settings = await getCompanySettings();
  if (!settings || !settings.bankAccountNumber) {
    return null;
  }

  return {
    bankName: settings.bankName,
    accountName: settings.bankAccountName,
    accountNumber: settings.bankAccountNumber,
    routingNumber: settings.bankRoutingNumber,
    swiftCode: settings.bankSwiftCode,
    bankAddress: settings.bankAddress,
  };
}

export const companySettingsService = {
  getCompanySettings,
  saveCompanySettings,
  initializeCompanySettings,
  addExpenseCategory,
  updateExpenseCategory,
  toggleExpenseCategoryStatus,
  getNextInvoiceNumber,
  getBankDetailsForInvoice,
};
